/* eslint-disable no-lonely-if */
import { CopilotActions } from 'actions/actionTypes';

import {
  MESSAGE_AUTHOR_TYPES,
  MESSAGE_STATUS_OPTIONS
} from 'constants/copilot';

const initialCopilotState = {
  sessions: {
    data: {}
  },
  fetching: false,
  loading: false,
  error: null,
  windowMode: 'embedded',
  chatContext: null
};

const copilot = (state = initialCopilotState, action) => {
  switch (action.type) {
    case CopilotActions.FETCH_COPILOT_CHAT_REQUEST: {
      return {
        ...state,
        fetching: true,
        loading: true,
        error: null
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_SUCCESS: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            ...action.response
          }
        },
        fetching: false,
        loading: false,
        error: null
      };
    }
    case CopilotActions.CREATE_COPILOT_CHAT_REQUEST:
    case CopilotActions.UPDATE_COPILOT_CHAT_NAME_REQUEST:
    case CopilotActions.DELETE_COPILOT_CHAT_REQUEST: {
      return {
        ...state,
        fetching: false,
        loading: true,
        error: null
      };
    }
    case CopilotActions.CREATE_COPILOT_CHAT_SUCCESS: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [action.response.uuid]: {
              ...action.response,
              contextType: action.contextType,
              contextUuid: action.contextUuid
            }
          }
        },
        fetching: false,
        loading: false,
        error: null
      };
    }
    case CopilotActions.UPDATE_COPILOT_CHAT_NAME_SUCCESS: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [action.response.uuid]: {
              ...state.sessions.data[action.response.uuid],
              name: action.response.name
            }
          }
        },
        fetching: false,
        loading: false,
        error: null
      };
    }
    case CopilotActions.DELETE_COPILOT_CHAT_SUCCESS: {
      const { [action.uuid]: removedSession, ...remainingSessions } =
        state.sessions.data;

      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: remainingSessions
        },
        fetching: false,
        loading: false,
        error: null
      };
    }
    case CopilotActions.FETCH_COPILOT_CHAT_FAILURE:
    case CopilotActions.CREATE_COPILOT_CHAT_FAILURE:
    case CopilotActions.UPDATE_COPILOT_CHAT_NAME_FAILURE:
    case CopilotActions.DELETE_COPILOT_CHAT_FAILURE: {
      return {
        ...state,
        fetching: false,
        loading: false,
        error: action.error
      };
    }
    case CopilotActions.RECEIVE_COPILOT_MESSAGE: {
      const { chatUuid, uuid, created } = action.data || {};

      const existingSession = state.sessions.data[chatUuid] || {};
      const messages = existingSession.messages || [];

      const messageIndex = messages.findIndex(
        message => message && message.uuid === uuid
      );

      const isStreamingMessage =
        action.data.status === MESSAGE_STATUS_OPTIONS.IN_PROGRESS;

      const isThinkingMessage =
        action.data.status === MESSAGE_STATUS_OPTIONS.THINKING;

      let newMessages;

      if (uuid) {
        if (messageIndex === -1) {
          // For new messages with UUID, insert at the correct position based on created timestamp
          const insertIndex = messages.findIndex(
            msg => new Date(msg.created || 0) > new Date(created || 0)
          );

          // If this is the first thinking message, initialize thinkingMessages array
          const messageData = isThinkingMessage
            ? {
                ...action.data,
                thinkingMessages: [
                  {
                    message: action.data.message || '',
                    timestamp: action.data.created || new Date().toISOString()
                  }
                ],
                thinkingStartTime: action.data.created,
                message: '' // Keep main message empty for thinking
              }
            : action.data;

          newMessages =
            insertIndex === -1
              ? [...messages, messageData] // Append to end if no higher timestamp found
              : [
                  ...messages.slice(0, insertIndex),
                  messageData,
                  ...messages.slice(insertIndex)
                ];
        } else {
          // Update existing message - but handle thinking messages differently
          if (isThinkingMessage) {
            // For thinking messages, accumulate in thinkingMessages array
            const existingMessage = messages[messageIndex];
            const existingThinkingMessages =
              existingMessage.thinkingMessages || [];
            const thinkingStartTime =
              existingMessage.thinkingStartTime || action.data.created;

            newMessages = [
              ...messages.slice(0, messageIndex),
              {
                ...messages[messageIndex],
                ...action.data,
                thinkingMessages: [
                  ...existingThinkingMessages,
                  {
                    message: action.data.message || '',
                    timestamp: action.data.created || new Date().toISOString()
                  }
                ],
                thinkingStartTime,
                message: existingMessage.message || '' // Preserve original message
              },
              ...messages.slice(messageIndex + 1)
            ];
          } else {
            // For regular streaming messages, keep thinking messages separate
            const existingMessage = messages[messageIndex];
            const wasPreviouslyThinking =
              existingMessage.status === MESSAGE_STATUS_OPTIONS.THINKING;
            const isNowNotThinking =
              wasPreviouslyThinking &&
              action.data.status !== MESSAGE_STATUS_OPTIONS.THINKING;

            newMessages = [
              ...messages.slice(0, messageIndex),
              {
                ...messages[messageIndex],
                ...action.data,
                thinkingMessages: existingMessage.thinkingMessages || [], // Preserve thinking messages
                thinkingStartTime: existingMessage.thinkingStartTime, // Preserve start time
                thinkingEndTime: isNowNotThinking
                  ? (() => {
                      // Use the completion message's timestamp as the end time
                      // Only set if it's a reliable timestamp (later than last step)
                      const completionTime = action.data.created;
                      const thinkingMessages =
                        existingMessage.thinkingMessages || [];

                      if (!completionTime || thinkingMessages.length === 0) {
                        // No completion time or no steps - let component capture time
                        return undefined;
                      }

                      // Check if completion time is reliable (after the last thinking step)
                      const lastStepTimestamp =
                        thinkingMessages[thinkingMessages.length - 1].timestamp;
                      const lastStepTime = new Date(
                        lastStepTimestamp
                      ).getTime();
                      const completionTimeMs = new Date(
                        completionTime
                      ).getTime();

                      // Only use server timestamp if it's reliable (later than last step)
                      if (completionTimeMs > lastStepTime) {
                        return completionTime;
                      }

                      // Fallback to current time if not reliable
                      return new Date().toISOString();
                    })()
                  : existingMessage.thinkingEndTime, // Preserve existing end time
                message: wasPreviouslyThinking
                  ? action.data.message || '' // Start fresh message content
                  : (existingMessage.message || '') +
                    (action.data.message || '') // Append to existing content
              },
              ...messages.slice(messageIndex + 1)
            ];
          }
        }
      } else {
        // For messages without UUID (like user messages), insert at the correct position
        const insertIndex = messages.findIndex(msg => {
          // If this is a Copilot message and the previous message is a user message,
          // ensure the Copilot message goes after the user message
          if (
            action.data.author === MESSAGE_AUTHOR_TYPES.COPILOT &&
            msg.author === MESSAGE_AUTHOR_TYPES.USER
          ) {
            return false; // Don't insert before user message
          }
          return new Date(msg.created || 0) > new Date(created || 0);
        });
        newMessages =
          insertIndex === -1
            ? [...messages, action.data] // Append to end if no higher timestamp found
            : [
                ...messages.slice(0, insertIndex),
                action.data,
                ...messages.slice(insertIndex)
              ];
      }

      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [chatUuid]: {
              ...existingSession,
              messages: newMessages
            }
          }
        },
        loading: isThinkingMessage || isStreamingMessage
      };
    }
    case CopilotActions.RECEIVE_COPILOT_NAME_UPDATE: {
      return {
        ...state,
        sessions: {
          ...state.sessions,
          data: {
            ...state.sessions.data,
            [action.data.chatUuid]: {
              ...state.sessions.data[action.data.chatUuid],
              name: action.data.name
            }
          }
        }
      };
    }
    case CopilotActions.MESSAGE_TIMEOUT: {
      return {
        ...state,
        fetching: false,
        loading: false
      };
    }
    case CopilotActions.SET_COPILOT_WINDOW_MODE: {
      return {
        ...state,
        windowMode: action.windowMode
      };
    }
    case CopilotActions.SET_COPILOT_CHAT_CONTEXT: {
      return {
        ...state,
        chatContext: action.chatContext
      };
    }
    case CopilotActions.EMPTY_COPILOT_CHAT_CONTEXT: {
      return {
        ...state,
        chatContext: null
      };
    }
    default:
      return state;
  }
};

export default copilot;
